import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const EMPTY = { data: [], paginator: null, loading: false, error: null }

/**
 * One paginated read (`{ data, paginator }`) behind a MyDataTable. Re-reads
 * whenever `fetchPage` changes (memoize it on its params); `fetchPage = null`
 * reads nothing. An answer that arrives after a newer request is dropped. A
 * failure is kept in `error` (the API's `{ status, message }`) so the page can
 * show it in place of the table, with `reload` as the retry. The object it
 * returns only changes when the read does (a memoized table can rely on it).
 */
export default function usePagedList(fetchPage) {
  const [state, setState] = useState({ ...EMPTY, loading: Boolean(fetchPage) })
  const latest = useRef(0)

  const reload = useCallback(async () => {
    latest.current += 1
    const request = latest.current
    if (!fetchPage) {
      setState(EMPTY)
      return
    }
    setState((prev) => ({ ...prev, loading: true, error: null }))
    try {
      const res = await fetchPage()
      if (request !== latest.current) return
      setState({ data: res?.data || [], paginator: res?.paginator || null, loading: false, error: null })
    } catch (err) {
      if (request !== latest.current) return
      setState({ ...EMPTY, error: err || { message: 'Something went wrong' } })
    }
  }, [fetchPage])

  useEffect(() => {
    reload()
  }, [reload])

  return useMemo(() => ({ ...state, reload }), [state, reload])
}
